import { useEffect, useMemo, useRef, useState } from "react";
import "./DropdownMultiSelect.css";

export default function DropdownMultiSelect({
  id,
  options = [],
  value = [],
  onChange,
  placeholder = "Select options",
  disabled = false,
  emptyLabel = "No options available",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = useMemo(() => (value || []).map(String), [value]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const selectedLabels = options
    .filter((opt) => selected.includes(String(opt.value)))
    .map((opt) => opt.label);

  const summary =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2} more`;

  const toggleValue = (optionValue) => {
    if (disabled) return;
    const next = selected.includes(optionValue)
      ? selected.filter((item) => item !== optionValue)
      : [...selected, optionValue];
    onChange(next);
  };

  return (
    <div
      className={`dropdown-multiselect ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
      ref={rootRef}
    >
      <button
        id={id}
        type="button"
        className="dropdown-multiselect__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span
          className={`dropdown-multiselect__summary ${
            selectedLabels.length === 0 ? "is-placeholder" : ""
          }`}
        >
          {summary}
        </span>
        <span className="dropdown-multiselect__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="dropdown-multiselect__menu" role="listbox" aria-multiselectable="true">
          {options.length === 0 ? (
            <p className="dropdown-multiselect__empty">{emptyLabel}</p>
          ) : (
            options.map((opt) => {
              const optionValue = String(opt.value);
              const checked = selected.includes(optionValue);
              return (
                <label
                  key={optionValue}
                  className={`dropdown-multiselect__option ${checked ? "is-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleValue(optionValue)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
